import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";

import DeleteModal from "components/shared/DeleteModal";
import AreYouSureModal from "components/shared/AreYouSureModal";
import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";
import { VehicleQRCode } from "components/qr-code";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import Button from "shared/components/FormElements/Button";
import CheckBox from "shared/components/FormElements/CheckBox";
import NotFound from "shared/components/UIElements/NotFound";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";
import { isActiveOptions, ownerShipTypes, yesNoUnknowTypes, plateTypes, vehiclePackageTypes } from "shared/util/types";
import DatePicker from "shared/components/FormElements/DatePicker";
import { PERMISSION_TYPES } from "@routebot/constants";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";

const InventoryVehicleItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDriverWarningModal, setShowDriverWarningModal] = useState(false);
  const [driverWarningPlate, setDriverWarningPlate] = useState("");
  const [showQRModal, setShowQRModal] = useState(false);
  const [isSyncingArventoNode, setIsSyncingArventoNode] = useState(false);
  const [arventoNode, setArventoNode] = useState("");

  const { vehicles, driverOptions, attendantOptions, UpdateDriver } = useContext(StoreContext);

  const itemType = "vehicle";
  const endPointUpdate = "vehicles";
  const endPointDelete = "vehicles";
  const endPointCreate = "vehicles";

  const isActiveOptionsLocal = isActiveOptions.map((option) => ({
    value: option.value,
    label: option.value ? t("inventoryVehicle.isActive.active") : t("inventoryVehicle.isActive.passive"),
  }));
  const ownerShipTypesLocal = ownerShipTypes.map((option) => ({
    value: option.value,
    label: t(`inventoryVehicle.ownership.${option.value}`),
  }));
  const plateTypesLocal = plateTypes.map((option) => ({
    value: option.value,
    label: t(`inventoryVehicle.plateTypes.${option.value}`),
  }));
  const vehiclePackageTypesLocal = vehiclePackageTypes.map((option) => ({
    value: option.value,
    label: t(`inventoryVehicle.vehiclePackageTypes.${option.value}`),
  }));
  const yesNoUnknowTypesLocal = yesNoUnknowTypes.map((option) => ({
    value: option.value,
    label: t(`inventoryVehicle.spareKey.${option.value}`),
  }));

  // Initialize form with all fields from the schema
  const [formState, inputHandler, setFormData] = useForm(
    {
      isActive: { value: "", isValid: true },
      plate: { value: null, isValid: false },
      ownership: { value: "", isValid: true },
      owner: { value: "", isValid: true },
      contact1: { value: "", isValid: true },
      address: { value: "", isValid: true },
      startDate: { value: "", isValid: true },
      startKm: { value: "", isValid: true },
      driver: { value: null, isValid: true },
      attendant: { value: null, isValid: true },
      startPhoto: { value: false, isValid: true },
      endDate: { value: "", isValid: true },
      endKm: { value: "", isValid: true },
      endPhoto: { value: "", isValid: true },
      notes: { value: "", isValid: true },
      department: { value: "", isValid: true },
      spareKey: { value: "", isValid: true },
      trackingSerialNo: { value: "", isValid: true },
      uttsSerialNo: { value: "", isValid: true },
      plateType: { value: "", isValid: true },
      vehiclePackage: { value: "", isValid: true },
      chassisNo: { value: "", isValid: true },
      modelYear: { value: "", isValid: true },
      marka: { value: "", isValid: true },
      model: { value: "", isValid: true },
      formalCapacity: { value: "", isValid: true },
      realCapacity: { value: "", isValid: true },
      generalInspectionDate: { value: "", isValid: true },
      generalInspectionAmount: { value: "", isValid: true },
      egzozInspectionDate: { value: "", isValid: true },
      egzozInspectionAmount: { value: "", isValid: true },
      trafficPolicyStartDate: { value: "", isValid: true },
      trafficPolicyEndDate: { value: "", isValid: true },
      trafficPolicyAmount: { value: "", isValid: true },
      insuranceStartDate: { value: "", isValid: true },
      insuranceEndDate: { value: "", isValid: true },
      insuranceAmount: { value: "", isValid: true },
      chairPolicyStartDate: { value: "", isValid: true },
      chairPolicyEndDate: { value: "", isValid: true },
      chairPolicyAmount: { value: "", isValid: true },
      roadDocumentC1Date: { value: "", isValid: true },
      roadDocumentC1Photo: { value: false, isValid: true },
      roadDocumentC2Date: { value: "", isValid: true },
      roadDocumentC2Photo: { value: false, isValid: true },
      d2Date: { value: "", isValid: true },
      d2Photo: { value: false, isValid: true },
      valilikDenetimKartiDate: { value: "", isValid: true },
      valilikDenetimKartiPhoto: { value: false, isValid: true },
      tursabLevhasiDate: { value: "", isValid: true },
      tursabLevhasiPhoto: { value: false, isValid: true },
      yakitOwner: { value: "", isValid: true },
      generalInspectionPhoto: { value: false, isValid: true },
      egzozInspectionPhoto: { value: false, isValid: true },
      trafficPolicyPhoto: { value: false, isValid: true },
      generalInspectionEndDate: { value: "", isValid: true },
      egzozInspectionEndDate: { value: "", isValid: true },
      chairPolicyPhoto: { value: false, isValid: true },
      ruhsatNo: { value: "", isValid: true },
      insurancePhoto: { value: false, isValid: true },
    },
    false,
  );

  // Fetching item data
  useEffect(() => {
    const fetchItem = async () => {
      if (!isNewItem) {
        setIsItemLoaded(false);
        try {
          const currentVehicle = vehicles.find((vehicle) => vehicle.id === itemId);
          if (currentVehicle) {
            // Populate form with existing data
            setLoadedItem(currentVehicle);
            setArventoNode(currentVehicle.arventoNode || "");
          }
        } catch (err) {
          console.log(err);
        }
        setIsItemLoaded(true);
      } else {
        // New item
        setIsItemLoaded(true);
      }
    };
    fetchItem();
  }, [itemId, vehicles, isNewItem, setFormData]);

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    const selectedDriverId = formState.inputs.driver.value?.toString?.() || formState.inputs.driver.value;
    const selectedDriverLabel = selectedDriverId
      ? driverOptions?.find((d) => d.value?.toString?.() === selectedDriverId)?.label
      : null;
    const isPlaceholderDriver = selectedDriverLabel && selectedDriverLabel.trim() === "-";

    if (selectedDriverId && !isPlaceholderDriver && vehicles?.length) {
      const otherVehicle = vehicles.find(
        (v) => v.driver && (v.driver?.toString?.() || v.driver) === selectedDriverId && v.id !== itemId,
      );
      if (otherVehicle) {
        setDriverWarningPlate(otherVehicle.plate);
        setShowDriverWarningModal(true);
        return;
      }
    }

    await performSave();
  };

  const showArventoSync =
    !isNewItem &&
    auth.permissions?.[PERMISSION_TYPES.ACCESS_TO_TELEMETRY] &&
    auth.company?.arventoConfig?.enabled;

  const trackingSerialNoValue = String(formState.inputs.trackingSerialNo.value || "").trim();
  const arventoNodeValue = String(arventoNode || "").trim();
  const isTrackingNodeMismatch =
    showArventoSync && (!trackingSerialNoValue || !arventoNodeValue || trackingSerialNoValue !== arventoNodeValue);

  const handleSyncArventoNode = async () => {
    if (!itemId || isSyncingArventoNode) return;
    setIsSyncingArventoNode(true);
    try {
      const data = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/telemetry/vehicle/${itemId}/sync-node`,
        "POST",
        JSON.stringify({}),
        { "Content-Type": "application/json" },
      );
      if (!data?.ok) {
        window.toast?.error?.(data?.message || t("anErrorOccurred"));
        return;
      }
      setArventoNode(data.arventoNode || "");
      if (loadedItem) {
        const updated = { ...loadedItem, arventoNode: data.arventoNode || "" };
        setLoadedItem(updated);
        UpdateItem(updated);
      }
      if (!data.foundInArvento) {
        window.toast?.warning?.(t("telemetryNodeNotInArvento"));
      } else {
        window.toast?.success?.(t("telemetryNodeSynced"));
      }
    } catch (err) {
      window.toast?.error?.(t("httpCodes." + err.code) || t("anErrorOccurred"));
    } finally {
      setIsSyncingArventoNode(false);
    }
  };

  const performSave = async () => {
    const endpoint = isNewItem
      ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
      : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

    const postType = isNewItem ? "POST" : "PATCH";

    // Build the object to send (spelled out)
    const infoToBeSent = {
      isActive: formState.inputs.isActive.value,
      plate: formState.inputs.plate.value,
      ownership: formState.inputs.ownership.value,
      owner: formState.inputs.owner.value,
      contact1: formState.inputs.contact1.value,
      address: formState.inputs.address.value,
      startDate: formState.inputs.startDate.value,
      startKm: formState.inputs.startKm.value,
      startPhoto: formState.inputs.startPhoto.value,
      endDate: formState.inputs.endDate.value,
      driver: formState.inputs.driver.value,
      attendant: formState.inputs.attendant.value,
      endKm: formState.inputs.endKm.value,
      endPhoto: formState.inputs.endPhoto.value,
      notes: formState.inputs.notes.value,
      department: formState.inputs.department.value,
      spareKey: formState.inputs.spareKey.value,
      trackingSerialNo: formState.inputs.trackingSerialNo.value,
      uttsSerialNo: formState.inputs.uttsSerialNo.value,
      plateType: formState.inputs.plateType.value,
      vehiclePackage: formState.inputs.vehiclePackage.value || "",
      chassisNo: formState.inputs.chassisNo.value,
      modelYear: formState.inputs.modelYear.value,
      marka: formState.inputs.marka.value,
      model: formState.inputs.model.value,
      formalCapacity: formState.inputs.formalCapacity.value,
      realCapacity: formState.inputs.realCapacity.value,
      generalInspectionDate: formState.inputs.generalInspectionDate.value,
      generalInspectionAmount: formState.inputs.generalInspectionAmount.value,
      egzozInspectionDate: formState.inputs.egzozInspectionDate.value,
      egzozInspectionAmount: formState.inputs.egzozInspectionAmount.value,
      trafficPolicyStartDate: formState.inputs.trafficPolicyStartDate.value,
      trafficPolicyEndDate: formState.inputs.trafficPolicyEndDate.value,
      trafficPolicyAmount: formState.inputs.trafficPolicyAmount.value,
      insuranceStartDate: formState.inputs.insuranceStartDate.value,
      insuranceEndDate: formState.inputs.insuranceEndDate.value,
      insuranceAmount: formState.inputs.insuranceAmount.value,
      chairPolicyStartDate: formState.inputs.chairPolicyStartDate.value,
      chairPolicyEndDate: formState.inputs.chairPolicyEndDate.value,
      chairPolicyAmount: formState.inputs.chairPolicyAmount.value,
      roadDocumentC1Date: formState.inputs.roadDocumentC1Date.value,
      roadDocumentC1Photo: formState.inputs.roadDocumentC1Photo.value,
      roadDocumentC2Date: formState.inputs.roadDocumentC2Date.value,
      roadDocumentC2Photo: formState.inputs.roadDocumentC2Photo.value,
      d2Date: formState.inputs.d2Date.value,
      d2Photo: formState.inputs.d2Photo.value,
      valilikDenetimKartiDate: formState.inputs.valilikDenetimKartiDate.value,
      valilikDenetimKartiPhoto: formState.inputs.valilikDenetimKartiPhoto.value,
      tursabLevhasiDate: formState.inputs.tursabLevhasiDate.value,
      tursabLevhasiPhoto: formState.inputs.tursabLevhasiPhoto.value,
      yakitOwner: formState.inputs.yakitOwner.value,
      generalInspectionPhoto: formState.inputs.generalInspectionPhoto.value,
      egzozInspectionPhoto: formState.inputs.egzozInspectionPhoto.value,
      trafficPolicyPhoto: formState.inputs.trafficPolicyPhoto.value,
      generalInspectionEndDate: formState.inputs.generalInspectionEndDate.value,
      egzozInspectionEndDate: formState.inputs.egzozInspectionEndDate.value,
      chairPolicyPhoto: formState.inputs.chairPolicyPhoto.value,
      ruhsatNo: formState.inputs.ruhsatNo.value,
      insurancePhoto: formState.inputs.insurancePhoto.value,
    };

    try {
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));
      const updatedVehicle = responseData?.vehicle ?? responseData?.[itemType];
      if (updatedVehicle) {
        if (isNewItem) {
          InsertItem(updatedVehicle);
          setGeneratedItemId(updatedVehicle.id);
        } else {
          UpdateItem(updatedVehicle);
          window.toast.success(t("updateSuccess"));
        }
      } else if (responseData && postType === "PATCH") {
        window.toast.error(t("anErrorOccurred"));
      }
    } catch (err) {
      window.toast.error(t("httpCodes." + err.code));
    }

    try {
      if (postType === "PATCH") {
        const usersEndpoint = `${process.env.REACT_APP_BACKEND_URL}/users/${formState.inputs.driver.value}`;
        const infos = {
          plate: formState.inputs.plate.value,
          email: formState.inputs.plate.value,
        };

        const responseData = await sendRequest(usersEndpoint, postType, JSON.stringify(infos));

        if (responseData?.user) {
          UpdateDriver(responseData?.user);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      window.toast.error(t("httpCodes." + err.code));
    }
  };

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  if (!isItemLoaded && !isNewItem) {
    return <p>Loading...</p>;
  }

  if (!isNewItem && isItemLoaded && !loadedItem && !error) {
    return <NotFound item={itemType} />;
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      <AreYouSureModal
        showModal={showDriverWarningModal}
        title={t("areYouSure")}
        text={t("inventoryVehicle.driverAlreadyOnAnotherVehicle", { plate: driverWarningPlate })}
        onClose={() => setShowDriverWarningModal(false)}
        onConfirmed={() => {
          setShowDriverWarningModal(false);
          performSave();
        }}
      />

      {isItemLoaded && (
        <form className="form-container" onSubmit={itemUpdateSubmitHandler}>
          {/* <a href={`https://drive.google.com/drive/folders/${loadedItem?.folder?.id}`} target="_blank" rel="noreferrer">
            Dosyalar
          </a> */}
          <div className="horizontal-flex">
            <CustomSelect
              options={isActiveOptionsLocal}
              id="isActive"
              onInput={inputHandler}
              label={t("inventoryVehicle.fields.isActive")}
              initialValue={
                isActiveOptionsLocal.find((option) => option.value.toString() === loadedItem?.isActive.toString())
                  ? isActiveOptionsLocal.find((option) => option.value.toString() === loadedItem?.isActive.toString())
                  : isActiveOptionsLocal[1]
              }
              validators={[VALIDATOR_REQUIRE()]}
              initialValid={true}
            ></CustomSelect>

            <Input
              id="plate"
              element="input"
              type="text"
              label={t("inventoryVehicle.fields.plate")}
              validators={[VALIDATOR_REQUIRE()]}
              errorText={t("inventoryVehicle.fields.plateError")}
              initialValue={loadedItem?.plate || ""}
              initialValid={true}
              onInput={inputHandler}
            />

            <CustomSelect
              options={plateTypesLocal}
              id="plateType"
              onInput={inputHandler}
              label={t("inventoryVehicle.fields.plateType")}
              initialValue={
                plateTypesLocal.find((option) => option.value.toString() === loadedItem?.plateType?.toString())
                  ? plateTypesLocal.find((option) => option.value.toString() === loadedItem?.plateType?.toString())
                  : plateTypesLocal[0]
              }
              validators={[VALIDATOR_NONE()]}
              initialValid={true}
            ></CustomSelect>

            <CustomSelect
              options={vehiclePackageTypesLocal}
              id="vehiclePackage"
              isClearable={true}
              onInput={inputHandler}
              label={t("inventoryVehicle.fields.vehiclePackage")}
              initialValue={
                vehiclePackageTypesLocal.find((option) => option.value === loadedItem?.vehiclePackage) || null
              }
              validators={[VALIDATOR_NONE()]}
              initialValid={true}
            ></CustomSelect>

            <Input
              id="department"
              element="input"
              type="text"
              label={t("inventoryVehicle.fields.department")}
              validators={[VALIDATOR_NONE()]}
              initialValue={loadedItem?.department || ""}
              initialValid={true}
              onInput={inputHandler}
            />

            <div style={{ flex: 2 }}>
              <CustomSelect
                options={driverOptions}
                id="driver"
                isMulti={false}
                onInput={inputHandler}
                label={t("driver")}
                initialValue={driverOptions.find((option) => option.value === loadedItem?.driver) || null}
                initialValid={true}
              ></CustomSelect>
            </div>

            <div style={{ flex: 2 }}>
              <CustomSelect
                options={attendantOptions}
                id="attendant"
                isMulti={false}
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.attendant")}
                initialValue={attendantOptions.find((option) => option.value === loadedItem?.attendant) || null}
                initialValid={true}
              ></CustomSelect>
            </div>
          </div>

          <div className="horizontal-flex" style={{ maxWidth: "33%", marginLeft: "0px" }}>
            <Input
              id="ruhsatNo"
              element="input"
              type="text"
              label={t("inventoryVehicle.fields.ruhsatNo")}
              validators={[VALIDATOR_NONE()]}
              initialValue={loadedItem?.ruhsatNo || ""}
              initialValid={true}
              onInput={inputHandler}
            />

            <Input
              id="yakitOwner"
              element="input"
              type="text"
              label={t("inventoryVehicle.fields.yakitOwner")}
              validators={[VALIDATOR_NONE()]}
              initialValue={loadedItem?.yakitOwner || ""}
              initialValid={true}
              onInput={inputHandler}
            />
          </div>
          {!isNewItem && (
            <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
              <div
                className="horizontal-flex"
                style={{ alignItems: "center", justifyContent: "flex-start", gap: "10px" }}
              >
                <div>
                  <Button type="button" href={`https://drive.google.com/drive/folders/${loadedItem?.folder?.id}`}>
                    {t("inventoryCommon.goToFiles")}
                  </Button>
                </div>
                {loadedItem?.plate && (
                  <div>
                    <Button type="button" onClick={() => setShowQRModal(true)}>
                      {t("printQRCode") || "QR Kod Yazdır"}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          <Input
            id="notes"
            element="textarea"
            type="text"
            label={t("inventoryVehicle.fields.notes")}
            rows={7}
            validators={[VALIDATOR_NONE()]}
            initialValue={loadedItem?.notes || ""}
            initialValid={true}
            onInput={inputHandler}
          />

          <div className="horizontal-flex">
            <div className="vertical-flex red-group-container" style={{ paddingTop: "15px", paddingBottom: "10px" }}>
              <CustomSelect
                options={ownerShipTypesLocal}
                id="ownership"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.ownership")}
                initialValue={
                  ownerShipTypesLocal.find((option) => option.value.toString() === loadedItem?.ownership?.toString())
                    ? ownerShipTypesLocal.find((option) => option.value.toString() === loadedItem?.ownership?.toString())
                    : ownerShipTypesLocal[0]
                }
                validators={[VALIDATOR_NONE()]}
                initialValid={true}
              ></CustomSelect>

              {/* <Input
                id="approvingPerson"
                element="input"
                type="text"
                label="Onaylayan"
                validators={[VALIDATOR_NONE()]}
                initialValue={formState.inputs.approvingPerson.value}
                initialValid={true}
                onInput={inputHandler}
              /> */}

              <Input
                id="owner"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.owner")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.owner || ""}
                initialValid={true}
                onInput={inputHandler}
              />
              <Input
                id="contact1"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.contact1")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.contact1 || ""}
                initialValid={true}
                onInput={inputHandler}
              />

              <Input
                id="address"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.address")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.address || ""}
                initialValid={true}
                onInput={inputHandler}
              />
            </div>

            <div className="vertical-flex">
              <div className="vertical-flex  red-group-container">
                <div className="horizontal-flex" style={{ alignItems: "center" }}>
                  <Input
                    id="modelYear"
                    element="input"
                    type="text"
                    label={t("inventoryVehicle.fields.modelYear")}
                    validators={[VALIDATOR_NONE()]}
                    initialValue={loadedItem?.modelYear || ""}
                    initialValid={true}
                    onInput={inputHandler}
                  />

                  <Input
                    id="marka"
                    element="input"
                    type="text"
                    label={t("inventoryVehicle.fields.marka")}
                    validators={[VALIDATOR_NONE()]}
                    initialValue={loadedItem?.marka || ""}
                    initialValid={true}
                    onInput={inputHandler}
                  />

                  <Input
                    id="model"
                    element="input"
                    type="text"
                    label={t("inventoryVehicle.fields.model")}
                    validators={[VALIDATOR_NONE()]}
                    initialValue={loadedItem?.model || ""}
                    initialValid={true}
                    onInput={inputHandler}
                  />
                </div>
              </div>
              <div className="vertical-flex  red-group-container">
                <div className="horizontal-flex" style={{ alignItems: "center" }}>
                  <DatePicker
                    id="startDate"
                    onInput={inputHandler}
                    label={t("inventoryVehicle.fields.startDate")}
                    initialValid={true}
                    validators={[VALIDATOR_NONE()]}
                    initialValue={loadedItem?.startDate?.toString() || ""}
                  ></DatePicker>

                  <Input
                    id="startKm"
                    element="input"
                    type="text"
                    label={t("inventoryVehicle.fields.startKm")}
                    validators={[VALIDATOR_NONE()]}
                    initialValue={loadedItem?.startKm || ""}
                    initialValid={true}
                    onInput={inputHandler}
                  />

                  <CheckBox
                    id="startPhoto"
                    label={t("inventoryVehicle.fields.startPhoto")}
                    initialValue={loadedItem?.startPhoto}
                    initialValid={true}
                    onInput={inputHandler}
                    validators={[VALIDATOR_NONE()]}
                  />
                </div>
              </div>

              <div className="vertical-flex  red-group-container">
                <div className="horizontal-flex" style={{ alignItems: "center" }}>
                  <DatePicker
                    id="endDate"
                    onInput={inputHandler}
                    label={t("inventoryVehicle.fields.endDate")}
                    initialValid={true}
                    validators={[VALIDATOR_NONE()]}
                    initialValue={loadedItem?.endDate?.toString() || ""}
                  ></DatePicker>

                  <Input
                    id="endKm"
                    element="input"
                    type="text"
                    label={t("inventoryVehicle.fields.endKm")}
                    validators={[VALIDATOR_NONE()]}
                    initialValue={loadedItem?.endKm || ""}
                    initialValid={true}
                    onInput={inputHandler}
                  />

                  <CheckBox
                    id="endPhoto"
                    label={t("inventoryVehicle.fields.endPhoto")}
                    initialValue={loadedItem?.endPhoto}
                    initialValid={true}
                    onInput={inputHandler}
                    validators={[VALIDATOR_NONE()]}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* <Input
            id="endReason"
            element="input"
            type="text"
            label="Çıkış Nedeni"
            validators={[VALIDATOR_NONE()]}
            initialValue={loadedItem?.endReason || ""}
            initialValid={true}
            onInput={inputHandler}
          /> */}

          <div className="horizontal-flex">
            <div className="vertical-flex red-group-container">
              <DatePicker
                id="generalInspectionDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.generalInspectionDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.generalInspectionDate?.toString() || ""}
              ></DatePicker>

              <DatePicker
                id="generalInspectionEndDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.generalInspectionEndDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.generalInspectionEndDate?.toString() || ""}
              ></DatePicker>

              <Input
                id="generalInspectionAmount"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.generalInspectionAmount")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.generalInspectionAmount || ""}
                initialValid={true}
                onInput={inputHandler}
              />

              <CheckBox
                id="generalInspectionPhoto"
                label={t("inventoryVehicle.fields.generalInspectionPhoto")}
                initialValue={loadedItem?.generalInspectionPhoto}
                initialValid={true}
                onInput={inputHandler}
                validators={[VALIDATOR_NONE()]}
              />
            </div>

            <div className="vertical-flex red-group-container">
              <DatePicker
                id="egzozInspectionDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.egzozInspectionDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.egzozInspectionDate?.toString() || ""}
              ></DatePicker>

              <DatePicker
                id="egzozInspectionEndDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.egzozInspectionEndDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.egzozInspectionEndDate?.toString() || ""}
              ></DatePicker>

              <Input
                id="egzozInspectionAmount"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.egzozInspectionAmount")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.egzozInspectionAmount || ""}
                initialValid={true}
                onInput={inputHandler}
              />

              <CheckBox
                id="egzozInspectionPhoto"
                label={t("inventoryVehicle.fields.egzozInspectionPhoto")}
                initialValue={loadedItem?.egzozInspectionPhoto}
                initialValid={true}
                onInput={inputHandler}
                validators={[VALIDATOR_NONE()]}
              />
            </div>

            <div className="vertical-flex red-group-container">
              <DatePicker
                id="trafficPolicyStartDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.trafficPolicyStartDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.trafficPolicyStartDate?.toString() || ""}
              ></DatePicker>

              <DatePicker
                id="trafficPolicyEndDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.trafficPolicyEndDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.trafficPolicyEndDate?.toString() || ""}
              ></DatePicker>

              <Input
                id="trafficPolicyAmount"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.trafficPolicyAmount")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.trafficPolicyAmount || ""}
                initialValid={true}
                onInput={inputHandler}
              />

              <CheckBox
                id="trafficPolicyPhoto"
                label={t("inventoryVehicle.fields.trafficPolicyPhoto")}
                initialValue={loadedItem?.trafficPolicyPhoto}
                initialValid={true}
                onInput={inputHandler}
                validators={[VALIDATOR_NONE()]}
              />
            </div>
            <div className="vertical-flex red-group-container">
              <DatePicker
                id="insuranceStartDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.insuranceStartDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.insuranceStartDate?.toString() || ""}
              ></DatePicker>

              <DatePicker
                id="insuranceEndDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.insuranceEndDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.insuranceEndDate?.toString() || ""}
              ></DatePicker>

              <Input
                id="insuranceAmount"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.insuranceAmount")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.insuranceAmount || ""}
                initialValid={true}
                onInput={inputHandler}
              />

              <CheckBox
                id="insurancePhoto"
                label={t("inventoryVehicle.fields.insurancePhoto")}
                initialValue={loadedItem?.insurancePhoto}
                initialValid={true}
                onInput={inputHandler}
                validators={[VALIDATOR_NONE()]}
              />
            </div>

            <div className="vertical-flex red-group-container">
              <DatePicker
                id="chairPolicyStartDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.chairPolicyStartDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.chairPolicyStartDate?.toString() || ""}
              ></DatePicker>

              <DatePicker
                id="chairPolicyEndDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.chairPolicyEndDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.chairPolicyEndDate?.toString() || ""}
              ></DatePicker>

              <Input
                id="chairPolicyAmount"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.chairPolicyAmount")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.chairPolicyAmount || ""}
                initialValid={true}
                onInput={inputHandler}
              />

              <CheckBox
                id="chairPolicyPhoto"
                label={t("inventoryVehicle.fields.chairPolicyPhoto")}
                initialValue={loadedItem?.chairPolicyPhoto}
                initialValid={true}
                onInput={inputHandler}
                validators={[VALIDATOR_NONE()]}
              />
            </div>
          </div>

          <div className="horizontal-flex"></div>

          <div className="horizontal-flex">
            <div className="vertical-flex red-group-container">
              <DatePicker
                id="roadDocumentC1Date"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.roadDocumentC1Date")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.roadDocumentC1Date?.toString() || ""}
              ></DatePicker>

              <CheckBox
                id="roadDocumentC1Photo"
                label={t("inventoryVehicle.fields.roadDocumentC1Photo")}
                initialValue={loadedItem?.roadDocumentC1Photo}
                initialValid={true}
                onInput={inputHandler}
                validators={[VALIDATOR_NONE()]}
              />
            </div>
            <div className="vertical-flex red-group-container">
              <DatePicker
                id="roadDocumentC2Date"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.roadDocumentC2Date")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.roadDocumentC2Date?.toString() || ""}
              ></DatePicker>

              <CheckBox
                id="roadDocumentC2Photo"
                label={t("inventoryVehicle.fields.roadDocumentC2Photo")}
                initialValue={loadedItem?.roadDocumentC2Photo}
                initialValid={true}
                onInput={inputHandler}
                validators={[VALIDATOR_NONE()]}
              />
            </div>

            <div className="vertical-flex red-group-container">
              <DatePicker
                id="d2Date"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.d2Date")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.d2Date?.toString() || ""}
              ></DatePicker>

              <CheckBox
                id="d2Photo"
                label={t("inventoryVehicle.fields.d2Photo")}
                initialValue={loadedItem?.d2Photo}
                initialValid={true}
                onInput={inputHandler}
                validators={[VALIDATOR_NONE()]}
              />
            </div>

            <div className="vertical-flex red-group-container">
              <DatePicker
                id="valilikDenetimKartiDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.valilikDenetimKartiDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.valilikDenetimKartiDate?.toString() || ""}
              ></DatePicker>

              <CheckBox
                id="valilikDenetimKartiPhoto"
                label={t("inventoryVehicle.fields.valilikDenetimKartiPhoto")}
                initialValue={loadedItem?.valilikDenetimKartiPhoto}
                initialValid={true}
                onInput={inputHandler}
                validators={[VALIDATOR_NONE()]}
              />
            </div>

            <div className="vertical-flex red-group-container">
              <DatePicker
                id="tursabLevhasiDate"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.tursabLevhasiDate")}
                initialValid={true}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.tursabLevhasiDate?.toString() || ""}
              ></DatePicker>

              <CheckBox
                id="tursabLevhasiPhoto"
                label={t("inventoryVehicle.fields.tursabLevhasiPhoto")}
                initialValue={loadedItem?.tursabLevhasiPhoto}
                initialValid={true}
                onInput={inputHandler}
                validators={[VALIDATOR_NONE()]}
              />
            </div>
          </div>

          <div className="horizontal-flex">
            <div
              className="vertical-flex red-group-container"
              style={isTrackingNodeMismatch ? { borderLeft: "3px solid #e53935", paddingLeft: 8 } : undefined}
            >
              <Input
                id="trackingSerialNo"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.trackingSerialNo")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.trackingSerialNo || ""}
                initialValid={true}
                onInput={inputHandler}
              />
              {showArventoSync ? (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 13, color: "#555", marginBottom: 4 }}>
                    {t("telemetryArventoNode")}:{" "}
                    <strong style={{ color: arventoNodeValue ? "#333" : "#999" }}>
                      {arventoNodeValue || "—"}
                    </strong>
                  </div>
                  {arventoNodeValue && trackingSerialNoValue ? (
                    <div
                      style={{
                        fontSize: 12,
                        marginBottom: 8,
                        color: isTrackingNodeMismatch ? "#e53935" : "#2e7d32",
                      }}
                    >
                      {isTrackingNodeMismatch ? t("telemetryNodeMismatch") : t("telemetryNodeMatch")}
                    </div>
                  ) : null}
                  <Button type="button" onClick={handleSyncArventoNode} disabled={isSyncingArventoNode || isLoading}>
                    {isSyncingArventoNode ? "…" : t("telemetryFetchArventoNode")}
                  </Button>
                </div>
              ) : null}
            </div>

            <div className="vertical-flex red-group-container">
              <CustomSelect
                options={yesNoUnknowTypesLocal}
                id="spareKey"
                onInput={inputHandler}
                label={t("inventoryVehicle.fields.spareKey")}
                initialValue={
                  yesNoUnknowTypesLocal.find((option) => option.value.toString() === loadedItem?.spareKey?.toString())
                    ? yesNoUnknowTypesLocal.find((option) => option.value.toString() === loadedItem?.spareKey?.toString())
                    : yesNoUnknowTypesLocal[0]
                }
                validators={[VALIDATOR_NONE()]}
                initialValid={true}
              ></CustomSelect>
            </div>

            <div className="vertical-flex red-group-container">
              <Input
                id="formalCapacity"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.formalCapacity")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.formalCapacity || ""}
                initialValid={true}
                onInput={inputHandler}
              />
            </div>

            <div className="vertical-flex red-group-container">
              <Input
                id="realCapacity"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.realCapacity")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.realCapacity}
                initialValid={true}
                onInput={inputHandler}
              />
            </div>
          </div>

          <div className="horizontal-flex" style={{ width: "49.5%", justifyContent: "flex-start", float: "left" }}>
            <div className="vertical-flex red-group-container">
              <Input
                id="uttsSerialNo"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.uttsSerialNo")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.uttsSerialNo || ""}
                initialValid={true}
                onInput={inputHandler}
              />
            </div>
            <div className="vertical-flex red-group-container">
              <Input
                id="chassisNo"
                element="input"
                type="text"
                label={t("inventoryVehicle.fields.chassisNo")}
                validators={[VALIDATOR_NONE()]}
                initialValue={loadedItem?.chassisNo || ""}
                initialValid={true}
                onInput={inputHandler}
              />
            </div>
          </div>
          {auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY] && (
            <FormFooter
              isNewItem={isNewItem}
              onUpdate={itemUpdateSubmitHandler}
              onDelete={() => setShowConfirmModal(true)}
              isUpdating={isLoading}
              disabled={!formState.isValid || !formState.inputs.plate.value || isLoading}
            />
          )}
        </form>
      )}

      {/* QR Kod Modal - Form dışında */}
      <VehicleQRCode plate={loadedItem?.plate || ""} isOpen={showQRModal} onClose={() => setShowQRModal(false)} />
    </React.Fragment>
  );
};

export default InventoryVehicleItem;
